import React, { useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import { buildGroupPayload, groupBobIds } from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { PlacementGrid, SaveNote } from "./awardFields";
import { padPlacements, preferScores } from "./dogLabel";
import { groupLabel } from "./partitionScores";
import { conformationOf } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  breedGroup: string;
  scores: IScore[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export const GroupAwardsCard: React.FC<IProps> = ({ breedGroup, scores, event, show, onSaved }) => {
  const saved = conformationOf(show)?.group_results?.find(
    (row) => row.breed_group === breedGroup && (row.stream || "regular") === "regular"
  );
  const bobIds = groupBobIds(show, breedGroup);

  const initial = padPlacements(saved?.placements);
  const [placements, setPlacements] = useState<string[]>(initial);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const formRef = useRef(initial);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const next = padPlacements(saved?.placements);
    setPlacements(next);
    formRef.current = next;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await apiService.saveConformationGroupResults(
          event._id,
          show._id,
          buildGroupPayload({ breedGroup, placements: formRef.current })
        );
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: "Failed to save group results.",
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const changePlacement = (index: number, id: string) => {
    setPlacements((prev) => {
      const next = [...prev];
      next[index] = id;
      formRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  return (
    <div className={styles.breedCard}>
      <div className={styles.breedCardHeader}>
        <span className={styles.breedName}>{groupLabel(breedGroup)}</span>
      </div>
      <PlacementGrid
        scores={preferScores(scores, bobIds)}
        value={placements}
        onChange={changePlacement}
      />
      <SaveNote status={saveStatus} />
    </div>
  );
};
